import { BadgeColor, IResortPathInfo } from 'api/resort/types';
import Link from 'next/link';
import { Routes } from 'routes';
import classNames from 'classnames';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { useSanitizedHtmlProps } from 'util/sanitized';
import { ChoiceBadge } from 'components/common/ChoiceBadge/ChoiceBadge';
import { getLocale } from 'i18n/locale';
import { ELocale } from 'i18n/localeEnum';
import { useResponsiveImage } from 'util/useResponsiveImage';
import { useMemo } from 'react';
import { useSettingsPrefetch } from 'settings/SettingsContext';
import styles from './style.module.scss';

interface IRegionHeroProps {
  regionTitle: string,
  title: string,
  description?: string,
  photo: string,
  photoSmall?: string,
  resort?: IResortPathInfo,
  logo?: boolean,
  snowWeather?: boolean,
  loading?: boolean,
  button?: {
    title: string,
    onClick: () => void,
  }
}

export const RegionHero = (
  {
    regionTitle,
    title,
    description,
    photo,
    photoSmall,
    resort,
    logo,
    snowWeather,
    button,
    loading,
  }: IRegionHeroProps,
) => {
  const prefetch = useSettingsPrefetch();
  const { t } = useOTSTranslation('common');
  const htmlProps = useSanitizedHtmlProps(description || '');
  const locale = getLocale() as ELocale;
  const bgImage = useResponsiveImage(photo || photoSmall || '', photoSmall || '');
  const color = BadgeColor.White;
  const style = useMemo(() => ({ backgroundImage: `url(${bgImage})` }), [bgImage]);

  return (
    <header className={styles.photo}>
      <div className={classNames(styles.fullBlurred, loading && styles.loading)}>
        <div style={style} />
      </div>
      <div className={classNames('container', styles.containerImage, loading && styles.loading)} style={style} />
      <div className="hero gradient" />
      <div className="hero mountains" />
      <div className="container">
        {resort && (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <Link href={Routes.resortSnowReport(resort)} prefetch={prefetch}>
            <a href={Routes.resortSnowReport(resort)} className={styles.region}>
              {`${resort.title}, ${regionTitle}`}
            </a>
          </Link>
        )}
        <div className={classNames(styles.title, logo ? styles.boxLogo : '')}>
          {logo && (
            <div className={styles.image}>
              <ChoiceBadge locale={locale} color={color} />
            </div>
          )}
          <h1>
            { snowWeather ? t('snowWeather.home.heroTitle') : title }
          </h1>
          {description && (
            <p className={styles.description}>
              <div {...htmlProps} />
            </p>
          )}
          {button && (
            <button
              type="button"
              className={styles.btn}
              onClick={button.onClick}
            >
              {button.title}
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
